/**
 * Build-A-Deal Rebate Selector (v5.5).
 *
 * Loads per-VIN selectable offers from /api/incentives/vehicle/{vin},
 * lets the customer pick compatible offers (compat checked via the
 * shared backend rule engine), and shows an "Estimated Price If
 * Qualified" line plus a required acknowledgment checkbox.
 *
 * Strict rules (per dealer/legal):
 *  - Never override vAuto advertised price.
 *  - Never auto-subtract any conditional offer.
 *  - Incompatible offers cannot be selected; clear reason shown.
 *  - Replace-offer flow when a new pick conflicts with current pick.
 *  - Acknowledgment required before BYOD lead can be submitted with offers.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Award, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const fmt = (n) =>
  n === null || n === undefined || isNaN(n) ? "—" : `$${Number(n).toLocaleString()}`;

// Canonical IDs / name patterns that classify an offer as a PUBLIC rebate
// available to everyone under program rules. Kept narrow on purpose — only
// truly-public GM cash programs qualify. Anything proof-required, private,
// or membership-gated is treated as conditional.
const PUBLIC_CANONICAL_IDS = new Set(["gmf_bonus_cash", "customer_cash", "bonus_cash"]);
const PUBLIC_NAME_RE = /(chevrolet\s+gmf\s+bonus\s+cash|gmf\s+bonus\s+cash|chevrolet\s+bonus\s+cash|consumer\s+cash|cash\s+allowance|customer\s+cash|bonus\s+cash)/i;

function isPublicEveryone(o) {
  if (!o) return false;
  if (o.proof_required === true) return false;
  if (o.private_offer === true) return false;
  if (PUBLIC_CANONICAL_IDS.has(String(o.incentive_id || "").toLowerCase())) return true;
  return PUBLIC_NAME_RE.test(String(o.name || ""));
}

export default function RebateSelector({
  vin,
  advertisedPrice,
  docFee,
  titleFee,
  dealType = "finance",
  paymentSummary,
  onChange,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]); // [{incentive_id, name, amount, proof_required, ...}]
  const [conflict, setConflict] = useState(null); // {candidate, reason, conflicts_with: [{id,name}]}
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState("");

  // v6.5.9 — Lock body scroll while the conflict modal is open so the page
  // underneath cannot scroll out from under the modal when the user moves
  // their mouse / wheel / trackpad. Uses the iOS-safe position:fixed pattern
  // so wheel events on any container can't scroll the page either.
  useEffect(() => {
    if (!conflict) return undefined;
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || html.scrollTop || 0;
    const prev = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
    };
    // Pin body in place so wheel/touch events can't move the page.
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [conflict]);

  useEffect(() => {
    if (!vin) return;
    let on = true;
    setLoading(true);
    setError("");
    setSelected([]);
    setAcknowledged(false);
    (async () => {
      try {
        const { data: d } = await api.get(`/incentives/vehicle/${vin}`);
        if (on) setData(d);
      } catch (e) {
        if (on) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => { on = false; };
  }, [vin]);

  // Notify parent of selection state
  useEffect(() => {
    const totalSavings = selected.reduce((s, o) => s + (o.amount || 0), 0);
    const finalBeforeTaxes = (advertisedPrice || 0) + (docFee || 0) + (titleFee || 0);
    const estimatedIfQualified = Math.max(0, finalBeforeTaxes - totalSavings);
    // v6.5.8 — surface selected Special APR so PaymentCalculator can override
    // the credit-tier rate + term when the customer picks an APR special.
    const aprPick = selected.find((s) => s.incentive_id === "apr_special" || s.kind === "apr");
    onChange?.({
      selected,
      total_savings_estimated: totalSavings,
      final_price_before_taxes: finalBeforeTaxes,
      estimated_price_if_qualified: estimatedIfQualified,
      acknowledged,
      blocked_attempts: [],
      selected_apr_pct: aprPick?.apr_pct ?? null,
      selected_apr_term: aprPick?.term_months ?? null,
      selected_apr_deferral_days: aprPick?.deferral_days ?? null,
      selected_apr_name: aprPick?.name ?? null,
    });
  }, [selected, advertisedPrice, docFee, titleFee, acknowledged, onChange]);

  // v6.5.6 — HARD DEFENSE LAYER (frontend).
  // Even if a stale cached API response, service worker, or future bug
  // accidentally puts one of these conditional public offers into
  // included_offers, we strip them out client-side before rendering the
  // "Already Included in Scott's Price" banner. These offers are NEVER
  // baked into the advertised price — they require the customer to opt in.
  const NEVER_ALREADY_INCLUDED_IDS = new Set([
    "gmf_bonus_cash",
    "customer_cash",
    "bonus_cash",
  ]);
  const NEVER_ALREADY_INCLUDED_NAME_RE = /(gmf\s+bonus\s+cash|chevrolet\s+gmf|chevrolet\s+bonus\s+cash|consumer\s+cash|customer\s+cash|bonus\s+cash)/i;
  const rawIncluded = data?.included_offers || [];
  const includedOffers = rawIncluded.filter((o) => {
    const id = String(o?.incentive_id || "").toLowerCase();
    if (NEVER_ALREADY_INCLUDED_IDS.has(id)) return false;
    if (NEVER_ALREADY_INCLUDED_NAME_RE.test(String(o?.name || ""))) return false;
    return true;
  });
  // If the API placed any of these in included_offers, hoist them to
  // selectable so the customer can opt in. (Defensive only; the backend
  // already enforces this in incentive_rules.py.)
  const hoistedSelectable = rawIncluded.filter((o) => {
    const id = String(o?.incentive_id || "").toLowerCase();
    if (NEVER_ALREADY_INCLUDED_IDS.has(id)) return true;
    if (NEVER_ALREADY_INCLUDED_NAME_RE.test(String(o?.name || ""))) return true;
    return false;
  }).map((o) => ({ ...o, included_in_price: false, selectable: true }));
  const rawSelectable = [...(data?.selectable_offers || []), ...hoistedSelectable];
  // Sort: PUBLIC offers first (available to everyone), then conditional.
  // Stable within each group on amount descending so the "best" rebate
  // appears at the top of its section.
  const selectableOffers = useMemo(() => {
    const arr = [...rawSelectable];
    arr.sort((a, b) => {
      const ap = isPublicEveryone(a) ? 0 : 1;
      const bp = isPublicEveryone(b) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return (b.amount || 0) - (a.amount || 0);
    });
    return arr;
  }, [rawSelectable]);
  const specialFinancing = data?.special_financing || [];

  const isSelected = (id) => selected.some((s) => s.incentive_id === id);

  const tryAdd = async (offer) => {
    setError("");
    setConflict(null);
    if (isSelected(offer.incentive_id)) {
      // toggle off
      setSelected((arr) => arr.filter((s) => s.incentive_id !== offer.incentive_id));
      return;
    }
    try {
      const { data: chk } = await api.post("/incentives/check", {
        candidate_id: offer.incentive_id,
        selected_ids: selected.map((s) => s.incentive_id),
        deal_type: dealType,
      });
      // v6.5.7 — Defensive: never let the compatibility backend tell us
      // GMF Bonus Cash / Customer Cash / Bonus Cash are "already included
      // in advertised price." These public offers are always opt-in.
      const FORCE_OK_IDS = new Set(["gmf_bonus_cash", "customer_cash", "bonus_cash"]);
      const isForcedOk =
        FORCE_OK_IDS.has(String(offer.incentive_id || "").toLowerCase()) &&
        !chk.ok &&
        /already.+included.+advertised|included in price/i.test(chk.reason || "");
      if (chk.ok || isForcedOk) {
        setSelected((arr) => [...arr, offer]);
      } else {
        // surface conflict modal for the replace-offer flow
        const conflictsDetail = (chk.conflicts_with || []).map((cid) => {
          const m = selected.find((s) => s.incentive_id === cid);
          return { id: cid, name: m?.name || cid };
        });
        setConflict({
          candidate: offer,
          reason: chk.reason,
          conflicts_with: conflictsDetail,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const replaceWith = () => {
    if (!conflict) return;
    const removeIds = conflict.conflicts_with.map((c) => c.id);
    setSelected((arr) => [
      ...arr.filter((s) => !removeIds.includes(s.incentive_id)),
      conflict.candidate,
    ]);
    setConflict(null);
  };

  const totals = useMemo(() => {
    const totalSavings = selected.reduce((s, o) => s + (o.amount || 0), 0);
    const finalBeforeTaxes = (advertisedPrice || 0) + (docFee || 0) + (titleFee || 0);
    const estimated = Math.max(0, finalBeforeTaxes - totalSavings);
    // Estimated payment impact — strict rules:
    //   • Only applies when there are selected (compatible) rebates with $ amount.
    //   • Payment scales linearly with principal at fixed APR + term.
    //   • Never modifies advertised price or Final Price Before Taxes.
    //   • Special financing is in a SEPARATE list, not summed here.
    //   • Included-in-price offers are excluded by /api/incentives/vehicle (not in selectable list).
    const baseMonthly = paymentSummary?.monthly_payment ?? paymentSummary?.payment ?? null;
    const amountFinanced = paymentSummary?.amount_financed ?? null;
    let monthlyIfQualified = null;
    if (baseMonthly && amountFinanced && amountFinanced > 0 && totalSavings > 0) {
      monthlyIfQualified = Math.max(
        0,
        baseMonthly * Math.max(0, (amountFinanced - totalSavings) / amountFinanced),
      );
    }
    return { totalSavings, finalBeforeTaxes, estimated, baseMonthly, monthlyIfQualified };
  }, [selected, advertisedPrice, docFee, titleFee, paymentSummary]);

  if (!vin) {
    return (
      <p className="text-sm text-[var(--ssc-ink-2)]" data-testid="rebate-no-vin">
        Pick a specific vehicle first — rebates are matched per VIN.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="rebate-selector">
      {loading && <p className="text-sm text-[var(--ssc-ink-2)]">Loading available offers...</p>}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2">{error}</div>
      )}

      {includedOffers.length > 0 && (
        <div
          className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm"
          data-testid="rebate-included-banner"
        >
          <div className="font-bold text-emerald-900 flex items-center gap-1.5">
            <CheckCircle2 size={14} /> Already Included in Scott&rsquo;s Price
          </div>
          <ul className="mt-1 ml-5 list-disc text-emerald-900">
            {includedOffers.map((o, i) => (
              <li key={i} data-testid={`rebate-included-${o.incentive_id || i}`}>
                {o.name}
                {o.amount ? ` · ${fmt(o.amount)}` : ""}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-[11px] text-emerald-900/80">
            These public rebates are already reflected in the advertised price.
            They cannot be subtracted again.
          </p>
        </div>
      )}

      <div data-testid="rebate-list-header" className="sr-only">
        Available Rebates and Conditional Offers
      </div>

      {selectableOffers.length === 0 ? (
        <p className="text-sm text-[var(--ssc-ink-2)]" data-testid="rebate-empty">
          No additional GM offers available on this VIN right now.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="rebate-list">
          {selectableOffers.map((o) => {
            const sel = isSelected(o.incentive_id);
            const isPublic = isPublicEveryone(o);
            return (
              <li
                key={o.incentive_id || o.name}
                className={`rounded-xl border p-3 flex flex-wrap items-center gap-3 ${sel ? "border-[var(--ssc-blue)] bg-blue-50" : "border-[var(--ssc-line)] bg-white"}`}
                data-testid={`rebate-item-${o.incentive_id || "unknown"}`}
                data-public={isPublic ? "1" : "0"}
              >
                <button
                  type="button"
                  onClick={() => tryAdd(o)}
                  className={`w-5 h-5 rounded grid place-items-center border-2 ${sel ? "bg-[var(--ssc-blue)] border-[var(--ssc-blue)] text-white" : "border-[var(--ssc-line)]"}`}
                  data-testid={`rebate-toggle-${o.incentive_id || "unknown"}`}
                  aria-pressed={sel}
                >
                  {sel && <CheckCircle2 size={14} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[var(--ssc-ink)] flex flex-wrap items-center gap-1.5">
                    <span>{o.name}</span>
                    {isPublic ? (
                      <span
                        className="text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300"
                        data-testid={`rebate-badge-public-${o.incentive_id || "unknown"}`}
                      >
                        Public · Available to Everyone
                      </span>
                    ) : (
                      <span
                        className="text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                        data-testid={`rebate-badge-conditional-${o.incentive_id || "unknown"}`}
                      >
                        May Require Qualification
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--ssc-ink-2)] flex flex-wrap gap-x-2 mt-0.5">
                    {o.amount ? <span className="font-bold text-[var(--ssc-navy)]">{fmt(o.amount)}</span> : null}
                    {o.proof_required && <span className="text-amber-700">May require proof of eligibility</span>}
                    {o.finance_only && <span className="text-blue-700">Finance only</span>}
                    {o.lease_only && <span className="text-blue-700">Lease only</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {specialFinancing.length > 0 && (
        <div className="rounded-xl border border-[var(--ssc-blue)]/30 bg-white px-3 py-3" data-testid="rebate-special-financing-section">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
            Special Financing Available
          </div>
          <p className="mt-1 text-[11px] text-[var(--ssc-ink-2)] leading-snug">
            Special APR offers reduce your monthly payment but typically cannot combine with finance cash bonuses
            (like Chevrolet GMF Bonus Cash). Picking one will prompt you to swap if there&rsquo;s a conflict.
          </p>
          <ul className="mt-3 space-y-2">
            {specialFinancing.map((f, i) => {
              const aprOffer = {
                incentive_id: "apr_special",
                name: f.apr_pct != null ? `${f.apr_pct}% APR for ${f.term_months} months` : "Special APR Offer",
                amount: 0,
                finance_only: true,
                proof_required: true, // credit approval required
                kind: "apr",
                apr_pct: f.apr_pct,
                term_months: f.term_months,
                deferral_days: f.deferral_days,
              };
              const sel = isSelected("apr_special");
              return (
                <li
                  key={i}
                  className={`rounded-lg border p-3 flex items-center gap-3 ${sel ? "border-[var(--ssc-blue)] bg-blue-50" : "border-[var(--ssc-line)] bg-white"}`}
                  data-testid={`rebate-special-item-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => tryAdd(aprOffer)}
                    className={`w-5 h-5 rounded grid place-items-center border-2 shrink-0 ${sel ? "bg-[var(--ssc-blue)] border-[var(--ssc-blue)] text-white" : "border-[var(--ssc-line)]"}`}
                    data-testid="rebate-toggle-apr_special"
                    aria-pressed={sel}
                  >
                    {sel && <CheckCircle2 size={14} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[var(--ssc-navy)]">{aprOffer.name}</div>
                    <div className="text-xs text-[var(--ssc-ink-2)] flex flex-wrap gap-x-2 mt-0.5">
                      {f.deferral_days ? <span>{f.deferral_days}-day payment deferral</span> : null}
                      <span className="text-blue-700">Finance only</span>
                      <span className="text-amber-700">Credit approval required</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Estimated savings panel */}
      <div className="rounded-xl border-2 border-[var(--ssc-navy)] bg-[var(--ssc-paper)] p-4" data-testid="rebate-estimate-panel">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="text-[var(--ssc-ink-2)]">Advertised Price</div>
          <div className="text-right tabular-nums">{fmt(advertisedPrice)}</div>
          <div className="text-[var(--ssc-ink-2)]">Doc Fee</div>
          <div className="text-right tabular-nums">+ {fmt(docFee)}</div>
          <div className="text-[var(--ssc-ink-2)]">Title Fee</div>
          <div className="text-right tabular-nums">+ {fmt(titleFee)}</div>
          <div className="font-extrabold text-[var(--ssc-navy)] border-t border-[var(--ssc-line)] pt-1">Final Price Before Taxes</div>
          <div className="text-right font-extrabold text-[var(--ssc-navy)] border-t border-[var(--ssc-line)] pt-1 tabular-nums">{fmt(totals.finalBeforeTaxes)}</div>
          {totals.totalSavings > 0 && (
            <>
              <div className="text-[var(--ssc-blue)] font-bold">Potential Additional Savings</div>
              <div className="text-right tabular-nums text-[var(--ssc-blue)] font-bold">− {fmt(totals.totalSavings)}</div>
              <div className="font-extrabold text-emerald-700 border-t-2 border-[var(--ssc-blue)] pt-1">Estimated Price If Qualified</div>
              <div className="text-right font-extrabold text-emerald-700 border-t-2 border-[var(--ssc-blue)] pt-1 tabular-nums" data-testid="rebate-estimated-price">{fmt(totals.estimated)}</div>
            </>
          )}
        </div>
        <p className="text-[11px] text-[var(--ssc-ink-2)] mt-3 leading-relaxed">
          Estimated price assumes selected offer eligibility and compatibility.
          Final pricing, eligibility, and incentive stacking must be confirmed by Byers Chevrolet.
        </p>

        {totals.monthlyIfQualified !== null && (
          <div
            className="mt-3 pt-3 border-t border-[var(--ssc-blue)]/40"
            data-testid="rebate-monthly-block"
          >
            <div className="grid grid-cols-2 gap-2 text-sm items-baseline">
              <div className="text-[var(--ssc-ink-2)]">Estimated Monthly Payment</div>
              <div className="text-right tabular-nums">{fmt(Math.round(totals.baseMonthly))}/mo</div>
              <div className="font-bold text-emerald-700">Estimated Monthly Payment If Qualified</div>
              <div className="text-right tabular-nums font-bold text-emerald-700" data-testid="rebate-monthly-if-qualified">
                {fmt(Math.round(totals.monthlyIfQualified))}/mo
              </div>
            </div>
            <p className="text-[10px] text-[var(--ssc-ink-2)] mt-2 leading-relaxed" data-testid="rebate-monthly-note">
              Estimate only. Final payment depends on approved credit, term, rate, taxes, fees, selected offers, and lender approval.
            </p>
          </div>
        )}
        {selected.length > 0 && (
          <label className="mt-3 flex items-start gap-2 text-sm" data-testid="rebate-acknowledge-wrap">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5"
              data-testid="rebate-acknowledge"
            />
            <span className="text-[var(--ssc-ink-2)]">
              I understand selected offers are estimates only and final eligibility, compatibility, and pricing must be confirmed by Byers Chevrolet.
            </span>
          </label>
        )}
      </div>

      {/* Replace offer modal — rendered via portal to document.body to escape
          any ancestor transform / filter / will-change that would otherwise
          turn the fixed-positioned modal into a relative-to-ancestor element. */}
      {conflict && createPortal(
        <div
          className="fixed inset-0 bg-black/55"
          style={{ zIndex: 2147483000 }}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.preventDefault()}
          data-testid="rebate-conflict-modal"
        >
          <div
            className="absolute w-[min(28rem,calc(100%-2rem))] rounded-2xl bg-white p-5"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
              transition: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={18} />
              <h3 className="font-extrabold text-[var(--ssc-navy)]">These offers can&rsquo;t be combined</h3>
            </div>
            <p className="mt-2 text-sm text-[var(--ssc-ink)]" data-testid="rebate-conflict-reason">
              <strong>{conflict.candidate?.name}</strong> can&rsquo;t be combined with
              {conflict.conflicts_with.length > 0
                ? ` ${conflict.conflicts_with.map((c) => c.name).join(", ")}.`
                : " your current selection."}{" "}
              {conflict.reason}
            </p>
            <p className="mt-2 text-xs text-[var(--ssc-ink-2)]">
              You can either keep what you already picked, or swap to <strong>{conflict.candidate?.name}</strong> — your choice.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {conflict.conflicts_with.length > 0 && (
                <button type="button" onClick={replaceWith} className="ssc-btn ssc-btn-primary" data-testid="rebate-conflict-replace">
                  Switch to {conflict.candidate.name}
                </button>
              )}
              <button type="button" onClick={() => setConflict(null)} className="ssc-btn ssc-btn-outline" data-testid="rebate-conflict-keep">
                Keep current offers
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
